/**
 * AR Technical Demo (F3 / GEN-2259) — Crestline vendor-portal UI store.
 *
 * THROWAWAY DEMO CODE. A tiny module-scoped external store (mirrors
 * `voice-call-store.ts`) holding the transient state for the mock customer
 * portal that Nami "operates" on the prospect's behalf (storyboard Flow 2,
 * scenes 7-9 & 11). It is purely representative — the real operator /
 * web-browser infra exists but is NOT called here.
 *
 * The portal screen plays a REAL screen recording of Nami driving Crestline's
 * vendor portal (one clip per flow), framed in browser chrome by
 * `<CrestlinePortalPage>`. Two flows:
 *  - BACKUPS pull (scenes 7-9): sign in → find #CR-DED-99214 → download the
 *    backup PDFs. When the clip ends the F0 `applyPortalBackupsFiled`
 *    checkpoint fires, so the backups land on deduction thread COL-1043.
 *  - CLAIM file (scene 11): open the dispute form → fill ($3,100, supporting
 *    docs) → submit. When the clip ends the F0 `applyClaimFiled` checkpoint
 *    fires.
 *
 * The scripted tools (`registerPortalTools.tsx`) navigate to the portal, then
 * `await waitForPortalVideoEnd()` so the narration + checkpoint only land once
 * the recording finishes. `<CrestlinePortalPage>` subscribes for reactive
 * rendering and calls `markPortalVideoEnded()` from the `<video>` `onEnded`.
 * A demo reset (`resetPortal`) clears it back to closed/idle.
 */

import { useCallback, useSyncExternalStore } from 'react';

/** Which portal flow is active. */
export type PortalMode = 'backups' | 'claim';

/** Whether the recorded agent session is still playing or has finished. */
export type PortalPhase = 'running' | 'done';

export interface PortalUiState {
  /** Whether the portal screen is showing at all. */
  open: boolean;
  mode: PortalMode;
  phase: PortalPhase;
  /** Bumped on every `openPortal` so the <video> remounts and replays. */
  runId: number;
}

const IDLE: PortalUiState = {
  open: false,
  mode: 'backups',
  phase: 'running',
  runId: 0,
};

let state: PortalUiState = { ...IDLE };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function set(patch: Partial<PortalUiState>): void {
  state = { ...state, ...patch };
  emit();
}

export function getPortalState(): PortalUiState {
  return state;
}

/** Open the portal for a flow, restarting the recording from the top. */
export function openPortal(mode: PortalMode): void {
  state = {
    ...IDLE,
    open: true,
    mode,
    phase: 'running',
    runId: state.runId + 1,
  };
  emit();
}

/** Close the portal overlay (leaves phase/runId; `resetPortal` clears fully). */
export function closePortal(): void {
  set({ open: false });
}

export function resetPortal(): void {
  state = { ...IDLE };
  emit();
}

// ---------------------------------------------------------------------------
// Video-completion bridge — lets the scripted (non-React) tool await the
// on-screen recording finishing. Resolves on the <video> `ended`/`error`
// event or after a safety timeout, so the flow never hangs on a missing or
// blocked video URL (e.g. before the S3 links are wired in).
// ---------------------------------------------------------------------------
let videoEndResolver: (() => void) | null = null;

export function waitForPortalVideoEnd(maxMs = 60_000): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      videoEndResolver = null;
      resolve();
    };
    videoEndResolver = finish;
    setTimeout(finish, maxMs);
  });
}

/** Called by the portal page when its recording ends (or errors). */
export function markPortalVideoEnded(): void {
  set({ phase: 'done' });
  videoEndResolver?.();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePortalStore<T>(selector: (s: PortalUiState) => T): T {
  const getSnapshot = useCallback(() => selector(state), [selector]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
